import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  PencilIcon,
  CheckIcon,
  ChatBubbleLeftEllipsisIcon,
  PaperClipIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ShareIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [timeTracking, setTimeTracking] = useState({ isActive: false, startTime: null, elapsed: 0 });
  const [newComment, setNewComment] = useState('');

  // Sample task data - in real app, this would be fetched based on ID
  const [task, setTask] = useState({
    id: parseInt(id),
    title: "Complete Annual Health Assessment",
    assignedBy: "Dr. Meera Gupta",
    assignedTo: "You",
    dueDate: "2025-07-25",
    priority: "High",
    status: "In Progress",
    description: "Annual mandatory health screening and assessment for all medical staff. This includes physical examination, vaccination status review, and health questionnaire completion.",
    project: "Healthcare Compliance",
    progress: 65,
    tags: ["Health", "Mandatory", "Annual"],
    estimatedHours: 4,
    actualHours: 2.5,
    createdDate: "2025-07-20",
    updatedDate: "2025-07-23",
    category: "Health & Safety",
    department: "Medical",
    attachments: [
      { id: 1, name: "Health_Assessment_Form.pdf", size: "245 KB", uploadedBy: "Dr. Meera Gupta" },
      { id: 2, name: "Vaccination_Guidelines.docx", size: "187 KB", uploadedBy: "Health Department" },
    ],
    subtasks: [
      { id: 1, title: "Complete health questionnaire", completed: true },
      { id: 2, title: "Schedule physical examination", completed: true },
      { id: 3, title: "Update vaccination records", completed: false },
      { id: 4, title: "Submit final assessment", completed: false },
    ],
  });

  const [activity, setActivity] = useState([
    {
      id: 1,
      type: "comment",
      user: "Dr. Meera Gupta",
      content: "Please prioritize completing this assessment by the end of the week. Let me know if you need any assistance with scheduling.",
      timestamp: "2025-07-23T10:30:00Z",
      avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 2,
      type: "status_change",
      user: "You",
      content: "Status changed from 'Pending' to 'In Progress'",
      timestamp: "2025-07-23T09:15:00Z",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 3,
      type: "subtask_completed",
      user: "You",
      content: "Completed subtask: Schedule physical examination",
      timestamp: "2025-07-22T14:20:00Z",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 4,
      type: "attachment",
      user: "Health Department",
      content: "Uploaded attachment: Vaccination_Guidelines.docx",
      timestamp: "2025-07-21T16:45:00Z",
      avatar: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=150&h=150&fit=crop&crop=face",
    },
  ]);

  // Time tracking effect
  useEffect(() => {
    let interval = null;
    if (timeTracking.isActive) {
      interval = setInterval(() => {
        setTimeTracking(prev => ({
          ...prev,
          elapsed: prev.elapsed + 1
        }));
      }, 1000);
    } else if (!timeTracking.isActive && timeTracking.elapsed !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timeTracking.isActive, timeTracking.elapsed]);

  // Helper functions
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-50 border-red-200';
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'In Progress': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Not Started': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleStartTracking = () => {
    setTimeTracking({
      isActive: true,
      startTime: new Date(),
      elapsed: 0
    });
  };

  const handlePauseTracking = () => {
    setTimeTracking(prev => ({
      ...prev,
      isActive: false
    }));
  };

  const handleStopTracking = () => {
    const hoursWorked = timeTracking.elapsed / 3600;
    setTask(prev => ({
      ...prev,
      actualHours: prev.actualHours + hoursWorked
    }));
    setTimeTracking({
      isActive: false,
      startTime: null,
      elapsed: 0
    });
  };

  const handleStatusChange = (newStatus) => {
    setTask(prev => ({ ...prev, status: newStatus }));
    const newActivity = {
      id: activity.length + 1,
      type: "status_change",
      user: "You",
      content: `Status changed to '${newStatus}'`,
      timestamp: new Date().toISOString(),
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    };
    setActivity(prev => [newActivity, ...prev]);
  };

  const handleSubtaskToggle = (subtaskId) => {
    setTask(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(subtask =>
        subtask.id === subtaskId ? { ...subtask, completed: !subtask.completed } : subtask
      )
    }));
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      const comment = {
        id: activity.length + 1,
        type: "comment",
        user: "You",
        content: newComment,
        timestamp: new Date().toISOString(),
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      };
      setActivity(prev => [comment, ...prev]);
      setNewComment('');
    }
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: ClockIcon },
    { id: 'activity', label: 'Activity', icon: ChatBubbleLeftEllipsisIcon },
    { id: 'attachments', label: 'Attachments', icon: PaperClipIcon },
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div className="flex items-start space-x-4">
              <button
                onClick={() => navigate('/tasks')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors mt-1"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{task.title}</h1>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
                  <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full border ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                  <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                    {task.priority} Priority
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    Due {formatDate(task.dueDate)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3 self-start sm:self-center">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <ShareIcon className="h-5 w-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Status Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
                <button
                  onClick={() => handleStatusChange('In Progress')}
                  disabled={task.status === 'In Progress'}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Start Task
                </button>
                <button
                  onClick={() => handleStatusChange('Completed')}
                  disabled={task.status === 'Completed'}
                  className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Mark Complete
                </button>
                <button
                  onClick={() => handleStatusChange('Pending')}
                  disabled={task.status === 'Pending'}
                  className="px-3 sm:px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Set Pending
                </button>
              </div>
            </div>

            {/* Time Tracking */}
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
                <h3 className="text-lg font-semibold text-gray-900">Time Tracking</h3>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  {timeTracking.isActive ? (
                    <>
                      <button
                        onClick={handlePauseTracking}
                        className="p-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        <PauseIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleStopTracking}
                        className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <StopIcon className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleStartTracking}
                      className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <PlayIcon className="h-4 w-4" />
                      <span>Start Timer</span>
                    </button>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="text-center p-3 sm:p-0">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">
                    {timeTracking.isActive ? formatTime(timeTracking.elapsed) : '00:00:00'}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">Current Session</div>
                </div>
                <div className="text-center p-3 sm:p-0">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">
                    {task.actualHours.toFixed(1)}h
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">Total Logged</div>
                </div>
                <div className="text-center p-3 sm:p-0">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">
                    {task.estimatedHours}h
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">Estimated</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="border-b">
                <nav className="flex overflow-x-auto px-4 sm:px-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <tab.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.label.substring(0, 1)}</span>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Description</h4>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{task.description}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Subtasks</h4>
                      <div className="space-y-2">
                        {task.subtasks.map((subtask) => (
                          <div key={subtask.id} className="flex items-start space-x-3">
                            <button
                              onClick={() => handleSubtaskToggle(subtask.id)}
                              className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors mt-0.5 ${
                                subtask.completed
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'border-gray-300 hover:border-green-500'
                              }`}
                            >
                              {subtask.completed && <CheckIcon className="h-3 w-3" />}
                            </button>
                            <span className={`${subtask.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                              {subtask.title}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Progress</h4>
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                            <span>Completion</span>
                            <span>{task.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${task.progress}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="p-1 text-gray-400 hover:text-gray-600">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {task.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full flex items-center space-x-1"
                          >
                            <TagIcon className="h-3 w-3" />
                            <span>{tag}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === 'activity' && (
                  <div className="space-y-6">
                    {/* Add Comment */}
                    <div>
                      <div className="flex space-x-3">
                        <img
                          className="h-8 w-8 rounded-full"
                          src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                          alt="Your avatar"
                        />
                        <div className="flex-1">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            rows="3"
                          />
                          <div className="mt-2 flex justify-end space-x-2">
                            <button
                              onClick={() => setNewComment('')}
                              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleAddComment}
                              disabled={!newComment.trim()}
                              className="px-4 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                              Comment
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-4">
                      {activity.map((item) => (
                        <div key={item.id} className="flex space-x-3">
                          <img
                            className="h-8 w-8 rounded-full"
                            src={item.avatar}
                            alt={`${item.user} avatar`}
                          />
                          <div className="flex-1">
                            <div className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="font-medium text-gray-900">{item.user}</div>
                                <div className="text-sm text-gray-500">
                                  {new Date(item.timestamp).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              </div>
                              <div className="text-gray-700">{item.content}</div>
                              {item.type !== 'comment' && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                    {item.type.replace('_', ' ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments Tab */}
                {activeTab === 'attachments' && (
                  <div className="space-y-4">
                    {task.attachments.length === 0 ? (
                      <div className="text-center py-8">
                        <PaperClipIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No attachments yet</p>
                        <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          Add Attachment
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <h4 className="text-lg font-semibold text-gray-900">
                            Attachments ({task.attachments.length})
                          </h4>
                          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            Add Attachment
                          </button>
                        </div>
                        <div className="space-y-3">
                          {task.attachments.map((attachment) => (
                            <div key={attachment.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <PaperClipIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{attachment.name}</div>
                                  <div className="text-sm text-gray-500">
                                    {attachment.size} • Uploaded by {attachment.uploadedBy}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                  <ShareIcon className="h-4 w-4" />
                                </button>
                                <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors">
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Task Info */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Information</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Assigned by</div>
                    <div className="font-medium text-gray-900">{task.assignedBy}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Assigned to</div>
                    <div className="font-medium text-gray-900">{task.assignedTo}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Due date</div>
                    <div className="font-medium text-gray-900">{formatDate(task.dueDate)}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Created</div>
                    <div className="font-medium text-gray-900">{formatDate(task.createdDate)}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <TagIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Project</div>
                    <Link
                      to={`/projects/${task.project.toLowerCase().replace(' ', '-')}`}
                      className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {task.project}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Summary */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Summary</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Subtasks Completed</span>
                    <span className="font-medium">
                      {task.subtasks.filter(st => st.completed).length} / {task.subtasks.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(task.subtasks.filter(st => st.completed).length / task.subtasks.length) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Time Progress</span>
                    <span className="font-medium">
                      {((task.actualHours / task.estimatedHours) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((task.actualHours / task.estimatedHours) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Tasks */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Tasks</h3>
              <div className="space-y-3">
                <Link
                  to="/tasks/2"
                  className="block p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <div className="font-medium text-gray-900 mb-1">Review Medical Protocols</div>
                  <div className="text-sm text-gray-500">Due July 30, 2025</div>
                  <div className="flex items-center mt-2">
                    <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Medium Priority
                    </span>
                  </div>
                </Link>
                
                <Link
                  to="/tasks/3"
                  className="block p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <div className="font-medium text-gray-900 mb-1">Quality Metrics Report</div>
                  <div className="text-sm text-gray-500">Due August 1, 2025</div>
                  <div className="flex items-center mt-2">
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                      High Priority
                    </span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TaskDetail;
